import { EnumChip } from '@/common/card/EnumChip';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useEnumDisplayNames } from '@/common/hooks/useEnumDisplayNames';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Chip, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

// Context strip: renders the Contract line when ctrtId is given, the Program line when progObj is given, and the
// Program Product line when progProdObj is given. Different pages show different subsets of this chain.
export const ProgContextStrip = ({ ctrtId, progObj, progProdObj }) => {
  const rsrc = useRsrc();
  const { navigateTo } = useNavState();
  const [ctrt, setCtrt] = useState(null);
  const { getEnumSpec: ctrtEnumSpec } = useEnumDisplayNames('md', 'CTRT');
  const { getEnumSpec: progEnumSpec } = useEnumDisplayNames('md', 'PROG');
  const { displayName: progProdEnumName } = useEnumDisplayNames('md', 'PROG_PROD');

  useEffect(() => {
    if (!ctrtId) return;
    rsrc.byId('md', 'CTRT', ctrtId).then(setCtrt);
  }, [ctrtId]);

  const showCtrt = Boolean(ctrtId && ctrt);
  const showProg = Boolean(progObj?.id);
  const showProgProd = Boolean(progProdObj?.id);
  if (!showCtrt && !showProg && !showProgProd) return null;

  const openCtrt = () => navigateTo('md/ctrt', { mode: Mode.DETAIL, entityId: ctrt.id });
  const openProg = () => navigateTo('md/prog', { mode: Mode.DETAIL, entityId: progObj.id });
  const openProgProd = () => navigateTo('md/prog-prod', { mode: Mode.DETAIL, entityId: progProdObj.id });

  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1 }}>
      <Stack spacing={0.5} divider={<Divider flexItem />}>
        {showCtrt && (
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <FontAwesomeIcon icon={fas.faFileContract} />
            <Typography variant="body2" color="text.secondary">
              Contract
            </Typography>
            <Link component="button" variant="body2" underline="hover" onClick={openCtrt} sx={{ fontWeight: 600 }}>
              {ctrt.code}
              {ctrt.name ? ` · ${ctrt.name}` : ''}
            </Link>
            <Detail label="Owner" value={ctrt.ownerCustName ?? ctrt.ownerCustCode} />
            <Detail label="Type" value={ctrt.typeCode} />
            <Detail label="Dates" value={`${DateUtils.format(ctrt.startDate)} – ${DateUtils.format(ctrt.endDate)}`} />
            <Box sx={{ ml: 'auto' }}>
              <EnumChip enumSpec={ctrtEnumSpec('status', ctrt.status)} code={ctrt.status} />
            </Box>
          </Stack>
        )}
        {showProg && (
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <FontAwesomeIcon icon={fas.faSitemap} />
            <Typography variant="body2" color="text.secondary">
              Program
            </Typography>
            <Link component="button" variant="body2" underline="hover" onClick={openProg} sx={{ fontWeight: 600 }}>
              {progObj.code}
              {progObj.name ? ` · ${progObj.name}` : ''}
            </Link>
            <Detail label="Customer" value={progObj.custName ?? progObj.custCode} />
            <Detail label="Type" value={progObj.progType} />
            <Detail
              label="Dates"
              value={`${DateUtils.format(progObj.startDate)} – ${DateUtils.format(progObj.endDate)}`}
            />
            {progObj.tierBased && <Chip size="small" variant="outlined" label="Tier based" />}
            <Box sx={{ ml: 'auto' }}>
              <EnumChip enumSpec={progEnumSpec('status', progObj.status)} code={progObj.status} />
            </Box>
          </Stack>
        )}
        {showProgProd && (
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <FontAwesomeIcon icon={fas.faPills} />
            <Typography variant="body2" color="text.secondary">
              Product
            </Typography>
            <Link component="button" variant="body2" underline="hover" onClick={openProgProd} sx={{ fontWeight: 600 }}>
              {progProdObj.code}
              {progProdObj.name ? ` · ${progProdObj.name}` : ''}
            </Link>
            <Detail label="Calculation" value={progProdEnumName('progProdCalcType', progProdObj.progProdCalcType)} />
            <Detail label="Basis" value={progProdEnumName('pymtBasisId', progProdObj.pymtBasisId)} />
            <Detail
              label="Dates"
              value={`${DateUtils.format(progProdObj.startDate)} – ${DateUtils.format(progProdObj.endDate)}`}
            />
            {progProdObj.valStatus && progProdObj.valStatus !== 'VALID' && progProdObj.valStatus !== 'NONE' && (
              <Chip size="small" color="warning" label={progProdObj.valStatus} sx={{ ml: 'auto' }} />
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const Detail = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <Typography variant="body2">
      <Typography component="span" variant="body2" color="text.secondary">
        {label}:{' '}
      </Typography>
      {value}
    </Typography>
  );
};
